<script setup lang="ts">
import { Upload } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';

const props = withDefaults(defineProps<{
  title?: string;
  description?: string;
  buttonLabel?: string;
  multiple?: boolean;
  accept?: string;
  disabled?: boolean;
  backgroundColor?: string;
}>(), {
  title: 'Drag and drop files here',
  description: 'You can also browse and upload multiple files in one go.',
  buttonLabel: 'Browse files',
  multiple: true,
  accept: '',
  disabled: false,
  backgroundColor: undefined,
});

const emit = defineEmits<{
  (e: 'files-selected', files: File[]): void;
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const fileInputKey = ref(0);
const isDragActive = ref(false);

function emitFiles(filesList: FileList | File[] | null | undefined) {
  if (!filesList || props.disabled) return;
  const files = Array.from(filesList).filter((file) => file.size > 0);
  emit('files-selected', files);
  fileInputKey.value += 1;
}

function openFilePicker() {
  if (props.disabled) return;
  fileInput.value?.click();
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  emitFiles(input.files);
}

function handleDragOver(event: DragEvent) {
  if (props.disabled) return;
  event.preventDefault();
  isDragActive.value = true;
}

function handleDragLeave(event: DragEvent) {
  if (props.disabled) return;
  event.preventDefault();
  if (event.currentTarget === event.target) {
    isDragActive.value = false;
  }
}

function handleDrop(event: DragEvent) {
  if (props.disabled) return;
  event.preventDefault();
  isDragActive.value = false;
  emitFiles(event.dataTransfer?.files);
}
</script>

<template>
  <div
    class="space-y-4 rounded-xl border border-dashed p-5 transition-colors"
    :class="[
      isDragActive ? 'border-foreground' : 'border-border',
      !backgroundColor ? (isDragActive ? 'bg-muted/40' : 'bg-background/40') : ''
    ]"
    :style="backgroundColor ? { backgroundColor } : undefined"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <input
      :key="fileInputKey"
      ref="fileInput"
      type="file"
      :multiple="multiple"
      :accept="accept"
      class="hidden"
      :disabled="disabled"
      @change="onFileSelected"
    >

    <div class="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div class="space-y-1">
        <p class="text-sm font-medium text-foreground">{{ title }}</p>
        <p class="text-xs text-muted-foreground">{{ description }}</p>
      </div>

      <Button type="button" variant="outline" :disabled="disabled" @click="openFilePicker">
        <Upload class="mr-2 size-4" />
        {{ buttonLabel }}
      </Button>
    </div>

    <slot />
  </div>
</template>
